/* ============================================================
   OrvoisRxUploader — shared prescription file uploader
   ------------------------------------------------------------
   A single place where "send the customer's prescription somewhere
   the merchant can open it" is implemented. Used by:

     - sections/lens-process.liquid  (new order flow, step 3)
     - assets/lens-config-cart.js    (Edit Configuration modal)

   WHY THIS EXISTS
   ---------------
   The theme's first implementation only kept { name, size, type }
   and dropped the File object, so the customer saw a thumbnail and
   the order carried `Prescription File: rx.jpg` while the bytes
   never left the browser. This module keeps the real File, streams
   it to a receiver, reports progress, and retries transient
   failures — so the property written to the cart can carry a link
   that actually resolves.

   CONFIGURATION
   -------------
   The storefront cannot talk to the Admin API (that would leak a
   token into page source), so an external receiver is required.
   Set it in ONE of two places:

     1. window.ORVOIS_RX_UPLOAD  — injected from Liquid, e.g.
          <script>window.ORVOIS_RX_UPLOAD = {
            provider: 'cloudinary',
            cloudName: 'xxx',
            uploadPreset: 'rx-prescriptions'
          };</script>
        (loaded BEFORE this asset)

     2. DEFAULTS below — edit in place if you prefer to keep the
        configuration in the asset.

   PROVIDERS
   ---------
     'cloudinary'  unsigned upload preset POST
                   https://api.cloudinary.com/v1_1/<cloud>/<type>/upload
                   -> { secure_url, public_id }

     'endpoint'    your own receiver (Cloudflare Worker / cloud
                   function / App Proxy). Must accept multipart POST
                   and answer JSON containing one of:
                     url | secure_url | file_url | key | id
                   Anything else is treated as a failure.

     'none'        nothing configured yet. Validation and local
                   preview still work; upload() resolves with
                   status 'unconfigured' and NO network call is made.

   API
   ---
     OrvoisRxUploader.version
     OrvoisRxUploader.configure(partialConfig)
     OrvoisRxUploader.config()                  -> frozen copy
     OrvoisRxUploader.provider()                -> 'cloudinary' | ...
     OrvoisRxUploader.isConfigured()            -> boolean
     OrvoisRxUploader.validate(file)            -> '' | message
     OrvoisRxUploader.formatSize(bytes)         -> '1.24 MB'
     OrvoisRxUploader.extension(fileName)       -> 'jpg'
     OrvoisRxUploader.upload(file, options)     -> Promise<result>
       options.onProgress(percent, attempt)
       options.onRetry(attempt, maxAttempts, error)

   Turnstile helpers (used by the theme only to pre-solve or reset):
     OrvoisRxUploader.turnstile.token()    -> Promise<token | ''>
     OrvoisRxUploader.turnstile.render()   -> Promise<widgetId | null>
     OrvoisRxUploader.turnstile.reset()    -> tears the widget down
     OrvoisRxUploader.turnstile.available()  -> false only if the script
                                               itself cannot load
     OrvoisRxUploader.turnstile.status()   -> { reason, code, healthy }

   RESULT SHAPE (never rejects — inspect `status`)
   ---
     {
       status: 'done' | 'failed' | 'unconfigured' | 'aborted',
       ok:     boolean,                 // status === 'done'
       name:   'rx.jpg',
       size:   123456,
       type:   'image/jpeg',
       ext:    'jpg',
       url:    'https://res.cloudinary.com/.../rx.jpg',  // '' unless done
       key:    'rx-prescriptions/abc123',                // '' unless done
       provider: 'cloudinary',
       attempts: 2,
       error:  '' | 'human readable message',
       code:   '' | 'network' | 'timeout' | 'turnstile' | 'validation' | ...
     }
   ============================================================ */
(function () {
  'use strict';

  if (window.OrvoisRxUploader && window.OrvoisRxUploader.version) return;

  var VERSION = 'v1.4';

  /* ------------------------------------------------------------
     DEFAULTS — edit here, or override via window.ORVOIS_RX_UPLOAD
     ------------------------------------------------------------ */
  var DEFAULTS = {
    provider: 'none',

    /* provider: 'cloudinary' */
    cloudName: '',
    uploadPreset: '',
    resourceType: 'auto',

    /* provider: 'endpoint' */
    endpoint: '',
    fieldName: 'file',
    headers: {},

    /* Turnstile — replaces a static upload key.

       A static key has to be shipped to the browser, so anyone can
       read it out of page source and replay it forever. A Turnstile
       token is solved per upload, single-use, and short-lived, so a
       token lifted from devtools is worthless. Nothing secret lives
       in this file as a result: the sitekey is public by design.

       Leave siteKey empty to skip the widget entirely (the receiver
       decides whether that is acceptable). */
    turnstileSiteKey: '',
    turnstileField: 'cf-turnstile-response',
    turnstileAction: '',
    turnstileTimeoutMs: 20000,

    /* A solved token is only reusable for so long. Turnstile tokens
       live ~300s; a token that solved during the idle warm-up and then
       sat there while the customer worked through the configurator is
       already dead by the time it is sent, and siteverify answers
       "timeout-or-duplicate" — indistinguishable, to the customer,
       from the widget never working at all. Anything older than this
       is thrown away and re-solved instead of sent. */
    turnstileFreshMs: 150000,
    turnstileContainerId: '',

    /* Whether configure() may fetch the Turnstile script and mount the
       widget ahead of time.

       Callers that handle their own file transfer and never call
       upload() (the native add-to-cart path) set this to false: the
       warm-up would otherwise pull a third-party script and park a
       badge in the page corner for a challenge this module will never
       solve. That is pure cost — bandwidth, a visible artefact and one
       more thing loading on the page — for no benefit.

       This only removes the idle speculation. Solving on demand still
       works when the flag is false, so a caller that changes its mind
       at runtime is not locked out. */
    turnstileWarm: true,

    /* Who is uploading. Sent as advisory form fields; the receiver
       stores them in the object's metadata so a future "customer may
       see their own prescription" check has something to match on —
       that link cannot be rebuilt after the fact.

       customerId comes from Liquid ({{ customer.id }}), so logged-out
       visitors send nothing. cartToken is read from Shopify's `cart`
       cookie when present; it is the key an order's cart_token can be
       joined against later. */
    customerId: '',
    cartToken: '',
    source: '',
    sendCartToken: true,

    /* shared */
    folder: '',
    maxBytes: 10 * 1024 * 1024,
    /* No heic/heif: iOS offers them, but they are not accepted downstream,
       so letting them through only moves the failure later. */
    allowedExt: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'pdf'],
    allowedMime: [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/gif',
      'application/pdf',
      'application/octet-stream'
    ],

    /* transient failures (network / 5xx / 429) are retried this many
       times in total, including the first attempt */
    maxAttempts: 3,
    retryDelayMs: 1200,
    timeoutMs: 90000,

    /* gate the storefront flow on the upload */
    blockOnError: true,        // a failed upload stops checkout
    blockWhenUnconfigured: false // 'none' provider stops checkout
  };

  var config = null;
  var activeRequest = null;

  function mergeConfig() {
    var out = {};
    var src = window.ORVOIS_RX_UPLOAD;

    Object.keys(DEFAULTS).forEach(function (k) {
      out[k] = DEFAULTS[k];
    });

    if (src && typeof src === 'object') {
      Object.keys(src).forEach(function (k) {
        if (k in DEFAULTS && src[k] !== undefined && src[k] !== null) {
          out[k] = src[k];
        }
      });
    }

    out.allowedExt = (out.allowedExt || []).map(function (e) {
      return String(e).toLowerCase().replace(/^\./, '');
    });
    out.allowedMime = (out.allowedMime || []).map(function (m) {
      return String(m).toLowerCase();
    });
    out.maxAttempts = Math.max(1, parseInt(out.maxAttempts, 10) || 1);
    out.retryDelayMs = Math.max(0, parseInt(out.retryDelayMs, 10) || 0);
    out.timeoutMs = Math.max(1000, parseInt(out.timeoutMs, 10) || 90000);
    out.maxBytes = Math.max(1, parseInt(out.maxBytes, 10) || 0);

    return out;
  }

  function getConfig() {
    if (!config) config = mergeConfig();
    return config;
  }

  /* ------------------------------------------------------------
     Small helpers
     ------------------------------------------------------------ */
  function extension(fileName) {
    var parts = String(fileName || '').toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
  }

  function formatSize(bytes) {
    bytes = Number(bytes) || 0;
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  function trimSlash(s) {
    return String(s || '').replace(/\/+$/, '');
  }

  function friendlySizeLimit(maxBytes) {
    return formatSize(maxBytes).replace(/\.00/, '');
  }

  /* ------------------------------------------------------------
     Turnstile

     Why not a static key: anything shipped to the browser is public.
     A key in this file could be read out of page source and replayed
     forever. Turnstile swaps it for a token that is solved per
     upload, single-use, and short-lived — copying it buys nothing.

     Mechanics worth knowing before touching this block:

       - The script is loaded lazily with ?render=explicit. Loading it
         on every page view would cost the customer bandwidth for a
         widget only the prescription step needs, so it is warmed in
         idle time instead.
       - appearance:'interaction-only' means a normal customer sees
         nothing at all; Cloudflare only draws a widget when it
         genuinely wants a human to act. That is why the default mount
         point can sit in a page corner without disturbing layout.
       - Only ONE token is live at a time. getResponse() is consumed
         immediately and reset() starts the next solve in the
         background, so a customer editing their prescription twice
         does not pay the solve latency twice.
       - Every failure path resolves with '' rather than rejecting.
         A blocked script or a failed challenge must not look like a
         broken uploader; the receiver is the authority on whether a
         missing token is acceptable.
     ------------------------------------------------------------ */
  var tsScript = null;      /* Promise<boolean> — script loaded */
  var tsWidgetId = null;    /* id of the widget currently mounted */
  var tsMountEl = null;     /* the element that widget was rendered into */
  var tsMountOwned = false; /* true when we created tsMountEl ourselves */
  var tsWaiter = null;      /* resolves the pending acquireTurnstileToken() */
  var tsDead = false;       /* the script itself will not load */
  var tsHealthy = true;     /* false once a solve fails */
  var tsGen = 0;            /* bumped per render; stale callbacks are ignored */
  var tsErrored = false;    /* the CURRENT widget reported an error */
  var tsReason = '';        /* WHY the last solve came back empty */
  var tsCode = '';          /* Turnstile's own error code, when it gives one */
  var tsTokenAt = 0;        /* when the cached token was issued */

  /* Two separate concerns live here, and getting either wrong makes the
     widget unusable for the rest of the page session:

     1. TEARDOWN must go through turnstile.remove(). Emptying the
        container with innerHTML *looks* like a cleanup but leaves
        Turnstile believing that element is still occupied, so the next
        render() on it fails — which turns one transient error into
        "verification never starts again, even after Retry". A fresh
        container per attempt plus remove() means a half-torn-down
        widget cannot leak into the next one.

     2. A FAILED SOLVE must not latch. Any error, timeout or blocked
        script marks the widget unhealthy, and the next attempt tears it
        down and starts over. Equally, a HEALTHY widget is left alone:
        the idle warm-up exists to remove solve latency, so a retry on a
        working widget must not throw that away. */
  function tsTeardown() {
    if (tsWidgetId !== null && window.turnstile) {
      try { window.turnstile.remove(tsWidgetId); } catch (e) { /* already gone */ }
    }
    tsWidgetId = null;
    if (tsMountOwned && tsMountEl && tsMountEl.parentNode) {
      tsMountEl.parentNode.removeChild(tsMountEl);
    }
    tsMountEl = null;
    tsMountOwned = false;
  }

  function tsReset() {
    tsTeardown();
    tsWaiter = null;
    tsErrored = false;
    tsHealthy = true;
    /* A <script> that never loaded resolves false forever; drop the
       cached promise so the next attempt injects a fresh tag. */
    if (tsDead && !window.turnstile) {
      tsScript = null;
      tsDead = false;
    }
  }

  /* Called before every attempt. A healthy widget is deliberately kept. */
  function tsRetry() {
    if (tsHealthy) return;
    tsReset();
  }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(true);
    if (tsScript) return tsScript;

    tsScript = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      s.onload = function () { resolve(!!window.turnstile); };
      s.onerror = function () { resolve(false); };
      document.head.appendChild(s);
    });
    return tsScript;
  }

  /* A fresh element for every render. Reusing one is what breaks after
     a failure: Turnstile keeps its own registry of mounted elements and
     only remove() clears it. */
  function tsNewMount() {
    var cfg = getConfig();

    if (cfg.turnstileContainerId) {
      var given = document.getElementById(cfg.turnstileContainerId);
      if (given) {
        tsMountEl = given;
        tsMountOwned = false;
        return given;
      }
    }

    var el = document.createElement('div');
    el.id = 'orvois-rx-turnstile';
    el.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:2147483646;';
    (document.body || document.documentElement).appendChild(el);
    tsMountEl = el;
    tsMountOwned = true;
    return el;
  }

  /* Records WHY a solve came back empty, so the failure can be
     explained to the customer instead of surfacing as a generic
     server error. A blocked script, a refused widget and an unfinished
     challenge look identical from the server's side and need different
     fixes, so the reason travels out to the message. */
  function tsDeliver(token) {
    var w = tsWaiter;
    tsWaiter = null;
    if (w) w(token || '');
  }

  function tsRender() {
    var cfg = getConfig();
    if (!cfg.turnstileSiteKey || tsDead) return Promise.resolve(null);
    if (tsWidgetId !== null) return Promise.resolve(tsWidgetId);

    return loadTurnstile().then(function (ok) {
      if (!ok || !window.turnstile) {
        tsDead = true;
        tsHealthy = false;
        tsReason = 'script_blocked';
        return null;
      }
      if (tsWidgetId !== null) return tsWidgetId;

      /* Callbacks are bound to this generation. A late error from an
         abandoned widget must not be mistaken for the live one's. */
      var gen = ++tsGen;
      tsErrored = false;
      tsReason = '';
      tsCode = '';

      var opts = {
        sitekey: cfg.turnstileSiteKey,
        appearance: 'interaction-only',
        callback: function (t) {
          if (gen !== tsGen) return;
          tsTokenAt = Date.now();
          tsReason = '';
          tsCode = '';
          tsHealthy = true;
          tsDeliver(t);
        },
        'error-callback': function (c) {
          if (gen !== tsGen) return;
          tsErrored = true;
          tsHealthy = false;
          tsReason = 'widget_error';
          tsCode = c === undefined || c === null ? '' : String(c);
          tsDeliver('');
        },
        'timeout-callback': function () {
          if (gen !== tsGen) return;
          tsHealthy = false;
          tsReason = tsReason || 'widget_timeout';
          tsDeliver('');
        }
      };
      if (cfg.turnstileAction) opts.action = cfg.turnstileAction;

      try {
        tsWidgetId = window.turnstile.render(tsNewMount(), opts);
      } catch (e) {
        tsHealthy = false;
        tsReason = 'render_failed';
        tsCode = (e && e.message) || '';
        tsTeardown();
        return null;
      }
      return tsWidgetId;
    });
  }

  function acquireTurnstileToken() {
    var cfg = getConfig();
    if (!cfg.turnstileSiteKey) return Promise.resolve('');

    return tsRender().then(function (id) {
      if (id === null) return '';

      /* error-callback can fire synchronously inside render() — an
         invalid site key is refused that way, before any challenge is
         attempted. At that point no waiter exists yet, so without this
         check we would sit here for the full timeout waiting for a
         token that is never coming. */
      if (tsErrored) {
        tsTeardown();
        return '';
      }

      var ready = window.turnstile.getResponse(id);
      /* Only a recently solved token is worth sending. The warm-up
         exists to remove latency, not to hoard a token across a long
         session: sending a stale one earns a rejection that looks
         exactly like "the widget never worked", and the customer has
         no way to tell. */
      var age = tsTokenAt ? Date.now() - tsTokenAt : Infinity;
      var fresh = !!ready && age <= cfg.turnstileFreshMs;

      if (ready) {
        /* Either way the widget starts solving the next one now. */
        try { window.turnstile.reset(id); } catch (e) { /* nothing to reset */ }
      }

      if (fresh) return ready;

      return new Promise(function (resolve) {
        var settled = false;
        var timer = setTimeout(function () {
          if (settled) return;
          settled = true;
          tsWaiter = null;
          tsHealthy = false;
          tsReason = tsReason || 'timeout';
          resolve('');
        }, cfg.turnstileTimeoutMs);

        tsWaiter = function (t) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(t || '');
        };
      });
    });
  }

  /* Who is uploading, as plain form fields. */
  function readCookie(name) {
    try {
      var m = document.cookie.match(
        new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
      );
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) {
      return '';
    }
  }

  function customerFields(extra) {
    var cfg = getConfig();
    var out = {};

    if (cfg.customerId) out.customerId = cfg.customerId;
    if (cfg.source) out.source = cfg.source;

    if (cfg.sendCartToken !== false) {
      out.cartToken = readCookie('cart') || cfg.cartToken || '';
    } else if (cfg.cartToken) {
      out.cartToken = cfg.cartToken;
    }

    if (extra && typeof extra === 'object') {
      Object.keys(extra).forEach(function (k) {
        if (extra[k] !== undefined && extra[k] !== null) out[k] = extra[k];
      });
    }
    return out;
  }

  /* ------------------------------------------------------------
     Validation — mirrors the copy shown under the drop zone
     ------------------------------------------------------------ */
  function validate(file) {
    var cfg = getConfig();

    if (!file) return 'Please choose a file to upload.';

    var ext = extension(file.name);
    var mime = String(file.type || '').toLowerCase();

    var extOk = cfg.allowedExt.indexOf(ext) !== -1;
    var mimeOk =
      !mime ||
      mime === 'application/octet-stream' ||
      cfg.allowedMime.indexOf(mime) !== -1;

    if (!extOk || !mimeOk) {
      return 'Unsupported file format. Please upload a JPG, PNG, WEBP or PDF file.';
    }

    if (!file.size) {
      return 'This file appears to be empty. Please try another file.';
    }

    if (file.size > cfg.maxBytes) {
      return (
        'This file is ' +
        formatSize(file.size) +
        '. The maximum upload size is ' +
        friendlySizeLimit(cfg.maxBytes) +
        '.'
      );
    }

    return '';
  }

  /* ------------------------------------------------------------
     Providers
     ------------------------------------------------------------ */

  /* Cloudinary unsigned upload. The preset name is public by
     design; allowed_formats / max_file_size / folder are enforced
     server-side, so a stranger cannot do more than fill the folder. */
  function cloudinaryRequest(file) {
    var cfg = getConfig();

    if (!cfg.cloudName || !cfg.uploadPreset) {
      return { error: 'Cloudinary is selected but cloudName / uploadPreset are missing.' };
    }

    var url =
      'https://api.cloudinary.com/v1_1/' +
      encodeURIComponent(cfg.cloudName) +
      '/' +
      encodeURIComponent(cfg.resourceType || 'auto') +
      '/upload';

    var fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', cfg.uploadPreset);
    if (cfg.folder) fd.append('folder', cfg.folder);

    return { url: url, body: fd, headers: {} };
  }

  /* Generic multipart receiver. Contract: JSON in, JSON out.

     The Turnstile token travels in the same request as the file, so
     the receiver can verify before it writes anything. Nothing queued
     here is a credential: the token is single-use and the receiver
     asks Cloudflare whether it is genuine. */
  function endpointRequest(file, token, extra) {
    var cfg = getConfig();

    if (!cfg.endpoint) {
      return { error: 'No upload endpoint configured.' };
    }

    var fd = new FormData();
    fd.append(cfg.fieldName || 'file', file, file.name);
    fd.append('filename', file.name);
    fd.append('size', String(file.size));
    if (cfg.folder) fd.append('folder', cfg.folder);
    if (token) fd.append(cfg.turnstileField || 'cf-turnstile-response', token);

    var identity = customerFields(extra);
    Object.keys(identity).forEach(function (k) {
      if (identity[k]) fd.append(k, String(identity[k]));
    });

    return { url: cfg.endpoint, body: fd, headers: cfg.headers || {} };
  }

  function pickUrl(data) {
    if (!data || typeof data !== 'object') return { url: '', key: '' };

    var url =
      data.url ||
      data.secure_url ||
      data.file_url ||
      data.location ||
      (data.data && (data.data.url || data.data.secure_url)) ||
      '';

    var key =
      data.key ||
      data.public_id ||
      data.id ||
      data.path ||
      (data.data && (data.data.key || data.data.public_id)) ||
      '';

    /* an endpoint may legitimately return only a key that the
       merchant-facing tool turns into a signed link later */
    if (!url && typeof key === 'string' && /^https?:\/\//i.test(key)) {
      url = key;
      key = '';
    }

    return { url: typeof url === 'string' ? url : '', key: typeof key === 'string' ? key : '' };
  }

  /* ------------------------------------------------------------
     Transport — XHR, because fetch() cannot report upload progress
     ------------------------------------------------------------ */
  /* Two steps whenever Turnstile is in play: obtain a token, then
     send. Kept split so a retry can reuse the flow without wrapping
     the XHR plumbing in another promise layer. */
  function sendOnce(file, onProgress, extra) {
    var cfg = getConfig();

    if (cfg.provider !== 'cloudinary' && cfg.provider !== 'endpoint') {
      return Promise.resolve({ unconfigured: true });
    }
    /* Cloudinary keeps its own unsigned-preset auth, and a store with
       no sitekey configured is the receiver's problem to police. */
    if (cfg.provider !== 'endpoint' || !cfg.turnstileSiteKey) {
      return deliverOnce(file, onProgress, extra, '');
    }
    /* Clear last attempt's latch first: the customer's Retry button
       must get a fresh solve, not a cached failure. */
    tsRetry();
    return acquireTurnstileToken().then(function (token) {
      return deliverOnce(file, onProgress, extra, token);
    });
  }

  function deliverOnce(file, onProgress, extra, token) {
    return new Promise(function (resolve, reject) {
      var cfg = getConfig();
      var spec;

      if (cfg.provider === 'cloudinary') spec = cloudinaryRequest(file);
      else spec = endpointRequest(file, token, extra);

      if (spec.error) {
        reject({ code: 'config', message: spec.error, retryable: false });
        return;
      }

      var xhr = new XMLHttpRequest();
      activeRequest = xhr;

      var settled = false;
      var timer = null;

      function finish(fn, payload) {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        activeRequest = null;
        fn(payload);
      }

      try {
        xhr.open('POST', spec.url, true);
      } catch (e) {
        finish(reject, {
          code: 'config',
          message: 'Could not reach the upload service.',
          retryable: false
        });
        return;
      }

      xhr.timeout = cfg.timeoutMs;

      /* no explicit Content-Type: the browser must add the multipart
         boundary itself */
      Object.keys(spec.headers || {}).forEach(function (h) {
        try {
          xhr.setRequestHeader(h, spec.headers[h]);
        } catch (e) {
          /* header the browser refuses to set — skip rather than fail */
        }
      });

      xhr.upload.addEventListener('progress', function (e) {
        if (!e.lengthComputable) return;
        var percent = Math.round((e.loaded / e.total) * 100);
        if (typeof onProgress === 'function') onProgress(percent);
      });

      xhr.addEventListener('load', function () {
        var data = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch (e) {
          data = null;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          if (data && data.error) {
            finish(reject, {
              code: 'provider',
              message: (data.error && data.error.message) || String(data.error),
              retryable: false
            });
            return;
          }
          finish(resolve, data || {});
          return;
        }

        /* 4xx is almost always a bad request we should not hammer;
           408 / 429 are worth another try */
        var retryable =
          xhr.status >= 500 || xhr.status === 408 || xhr.status === 429;

        var explained = explainFailure(data, xhr.status);

        finish(reject, {
          code: explained.code,
          status: xhr.status,
          message: explained.message,
          retryable: retryable
        });
      });

      xhr.addEventListener('error', function () {
        finish(reject, {
          code: 'network',
          message: 'Network error while uploading. Check your connection and try again.',
          retryable: true
        });
      });

      xhr.addEventListener('timeout', function () {
        finish(reject, {
          code: 'timeout',
          message: 'The upload timed out. Please try again.',
          retryable: true
        });
      });

      xhr.addEventListener('abort', function () {
        finish(reject, {
          code: 'aborted',
          message: 'Upload cancelled.',
          retryable: false
        });
      });

      try {
        xhr.send(spec.body);
      } catch (e) {
        finish(reject, {
          code: 'network',
          message: 'Could not start the upload. Please try again.',
          retryable: true
        });
      }
    });
  }

  function delay(ms) {
    return new Promise(function (r) {
      setTimeout(r, ms);
    });
  }

  /* ------------------------------------------------------------
     Turning a refusal into something the customer can act on.

     The receiver answers a bad upload with a stable machine code and,
     for Turnstile, a reason. Those two Turnstile reasons look the same
     to a customer and need opposite fixes:

       missing_token     the widget never solved in this browser, so no
                         token was sent. Usually an ad blocker eating
                         challenges.cloudflare.com, or a challenge that
                         was never completed. Retrying helps.
       verify_rejected   a token DID arrive and Cloudflare refused it —
                         wrong secret, already used, or expired. That is
                         ours to fix, not theirs, so the codes are shown.

     tsLastReason adds the client-side half of the diagnosis, which the
     receiver cannot see.
     ------------------------------------------------------------ */
  /* Turnstile's error codes are the only thing that separates the usual
     suspects, and they are easy to throw away — the previous version
     accepted the code argument and dropped it, which left "the widget
     did not run" as the entire diagnosis. 400020 is what a site key
     that this hostname is not allowed to use comes back as. */
  var TS_CODE_HINTS = {
    '400020': ' The site key is not accepted for this domain.',
    /* 110200 is "domain not authorized": the widget only runs on the
       hostnames it was created for. On a shop domain that is our
       problem and the customer's only move is to retry; on a dev host
       it is expected, and saying so saves the next person the hour it
       took to work out why nothing ever solved. */
    '110200': ' The verification widget is not authorized for this domain — ' +
              'this is a configuration problem on our side, not something you can fix. ' +
              'Please try again, and tell us if it keeps happening.',
    '110420': ' The site key is not accepted for this domain.',
    '110430': ' This domain is not allowed for the site key.'
  };

  var TS_DEV_HINT =
    ' This page is being served from a development host, which is not in the ' +
    'widget\u2019s hostname list, so the check can never start here — open the ' +
    'store\u2019s own domain instead of the local dev server.';

  /* Dev hosts are the one case where 110200 is not a misconfiguration:
     it is the expected result of previewing a theme from a local
     server. Recognising them keeps the customer-facing wording intact
     while giving a developer an instruction rather than a dead end. */
  function isDevHost(host) {
    if (!host) return false;
    return (
      host === 'localhost' ||
      host === '[::1]' ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(host) ||
      /\.localhost$/.test(host) ||
      /\.local$/.test(host)
    );
  }

  /* The half of the diagnosis the receiver cannot see: whether the
     script loaded here, what the widget said, and which hostname the
     customer is actually on. Hostname matters because a site key is
     only valid on the domains it was created for, and the same store
     answers on more than one. */
  function tsHost() {
    try { return window.location.hostname; } catch (e) { return ''; }
  }

  function tsTrace() {
    var host = tsHost();
    var bits = [];
    if (host) bits.push('site ' + host);
    if (tsReason) bits.push(tsReason);
    if (tsCode) bits.push('code ' + tsCode);
    return bits.length ? ' [' + bits.join(' \u00b7 ') + ']' : '';
  }

  function tsCodeHint() {
    return tsCode && TS_CODE_HINTS[tsCode] ? TS_CODE_HINTS[tsCode] : '';
  }

  function explainFailure(data, status) {
    var raw = (data && (data.error || data.message)) || '';
    var reason = data && data.reason;
    var codes = (data && data.detail) || [];
    var suffix = codes.length ? ' (' + codes.join(', ') + ')' : '';
    var fallback = 'Upload failed (HTTP ' + status + ').';

    if (raw === 'turnstile_failed' || reason === 'missing_token' || reason === 'verify_rejected') {
      /* Keep the full picture in the console: the message shown to the
         customer is deliberately short, and support will ask for this. */
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[rx-uploader] turnstile refused the upload', {
          serverReason: reason || raw,
          serverDetail: codes,
          clientReason: tsReason || '(none)',
          clientCode: tsCode || '(none)',
          host: tsHost(),
          devHost: isDevHost(tsHost())
        });
      }

      if (reason === 'missing_token') {
        var msg;

        if (tsReason === 'script_blocked') {
          msg =
            'Human verification could not load, so the file was not uploaded. ' +
            'Something is blocking challenges.cloudflare.com — a browser extension, ' +
            'a privacy setting or a network filter. Allow it, or try another browser.';
        } else if (tsReason === 'widget_error') {
          msg =
            'Human verification could not start, so the file was not uploaded.' +
            (tsCode === '110200' && isDevHost(tsHost()) ? TS_DEV_HINT : tsCodeHint());
        } else if (tsReason === 'render_failed') {
          msg =
            'Human verification could not start in this browser, ' +
            'so the file was not uploaded.';
        } else if (tsReason === 'widget_timeout' || tsReason === 'timeout') {
          msg =
            'Human verification did not finish in time, so the file was not uploaded. ' +
            'A verification box appears in the bottom-right corner of the page — ' +
            'tick it, then upload again.';
        } else {
          msg =
            'Human verification did not complete, so the file was not uploaded. ' +
            'A verification box may appear in the bottom-right corner of the page; ' +
            'complete it and upload again.';
        }

        return { code: 'turnstile', message: msg + tsTrace() };
      }
      if (reason === 'verify_rejected') {
        return {
          code: 'turnstile',
          message:
            'Cloudflare rejected the verification token' +
            suffix +
            '. Please retry the upload.' +
            tsTrace()
        };
      }
      return {
        code: 'turnstile',
        message:
          'Human verification could not be confirmed' +
          suffix +
          '. Please retry the upload.' +
          tsTrace()
      };
    }

    /* The receiver's own sentence beats its machine code when it has one. */
    if (data && typeof data.message === 'string' && data.message) {
      return { code: 'http', message: data.message };
    }
    return { code: 'http', message: raw || fallback };
  }

  function baseResult(file) {
    return {
      status: 'failed',
      ok: false,
      name: file && file.name ? file.name : '',
      size: file && file.size ? file.size : 0,
      type: (file && file.type) || '',
      ext: extension(file && file.name),
      url: '',
      key: '',
      provider: getConfig().provider,
      attempts: 0,
      error: '',
      code: ''
    };
  }

  /* ------------------------------------------------------------
     upload() — never rejects. Inspect result.status.
     ------------------------------------------------------------ */
  function upload(file, options) {
    options = options || {};
    var cfg = getConfig();
    var result = baseResult(file);

    var validationError = validate(file);
    if (validationError) {
      result.status = 'failed';
      result.error = validationError;
      result.code = 'validation';
      return Promise.resolve(result);
    }

    if (!isConfigured()) {
      result.status = 'unconfigured';
      result.provider = 'none';
      result.error =
        'Prescription upload is not enabled on this store yet — the file was not sent.';
      result.code = 'unconfigured';
      return Promise.resolve(result);
    }

    if (typeof options.onProgress === 'function') {
      options.onProgress(0, 1);
    }

    var attempt = 0;

    function tryOnce() {
      attempt += 1;
      result.attempts = attempt;

      return sendOnce(file, options.onProgress, options.meta).then(
        function (data) {
          if (data && data.unconfigured) {
            result.status = 'unconfigured';
            result.error =
              'Prescription upload is not enabled on this store yet — the file was not sent.';
            result.code = 'unconfigured';
            return result;
          }

          var picked = pickUrl(data);

          if (!picked.url && !picked.key) {
            throw {
              code: 'provider',
              message: 'The upload service did not return a file location.',
              retryable: false
            };
          }

          result.status = 'done';
          result.ok = true;
          result.url = picked.url;
          result.key = picked.key;
          result.error = '';
          result.code = '';
          if (typeof options.onProgress === 'function') {
            options.onProgress(100, attempt);
          }
          return result;
        },
        function (err) {
          var retryable = err && err.retryable && attempt < cfg.maxAttempts;

          if (!retryable) {
            if (err && err.code === 'aborted') result.status = 'aborted';
            else result.status = 'failed';
            result.error = (err && err.message) || 'Upload failed.';
            result.code = (err && err.code) || 'unknown';
            return result;
          }

          if (typeof options.onRetry === 'function') {
            options.onRetry(attempt + 1, cfg.maxAttempts, err);
          }

          if (typeof options.onProgress === 'function') {
            options.onProgress(0, attempt + 1);
          }

          return delay(cfg.retryDelayMs * attempt).then(tryOnce);
        }
      );
    }

    return Promise.resolve().then(tryOnce).catch(function (err) {
      result.status = 'failed';
      result.error = (err && err.message) || 'Upload failed.';
      result.code = (err && err.code) || 'unknown';
      return result;
    });
  }

  function abort() {
    if (activeRequest) {
      try {
        activeRequest.abort();
      } catch (e) {
        /* already finished */
      }
      activeRequest = null;
    }
  }

  /* ------------------------------------------------------------
     Public surface
     ------------------------------------------------------------ */
  window.OrvoisRxUploader = {
    version: VERSION,
    upload: upload,
    validate: validate,
    abort: abort,
    formatSize: formatSize,
    extension: extension,
    config: function () {
      return getConfig();
    },
    provider: function () {
      return getConfig().provider;
    },
    isConfigured: isConfigured,
    configure: function (partial) {
      window.ORVOIS_RX_UPLOAD = Object.assign(
        {},
        window.ORVOIS_RX_UPLOAD || {},
        partial || {}
      );
      config = null;
      var cfg = getConfig();

      /* Warm the widget during idle time. Solving on demand would put
         the challenge between the customer and their progress bar,
         which reads as a stall; solving early makes it free.

         Skipped when the caller says the widget will never be used
         (`turnstileWarm: false`): no script is fetched and no widget is
         mounted, so nothing shows up in the page corner for a flow that
         does its own file handling. */
      if (cfg.turnstileWarm !== false && cfg.turnstileSiteKey && !tsDead) {
        var warm = function () { tsRender(); };
        if (window.requestIdleCallback) {
          window.requestIdleCallback(warm, { timeout: 4000 });
        } else {
          setTimeout(warm, 1500);
        }
      }
      return cfg;
    },
    /** Turnstile helpers, for callers that want to pre-solve or reset. */
    turnstile: {
      render: tsRender,
      token: acquireTurnstileToken,
      /* Only a script that cannot load is fatal; a widget that failed
         once is torn down and rebuilt on the next attempt. */
      available: function () { return !tsDead; },
      reset: tsReset,
      /* What happened on this side of the wire, for support and for the
         failure message. The server never sees any of it. */
      status: function () {
        return { reason: tsReason, code: tsCode, healthy: tsHealthy };
      }
    },
    /** Should the storefront refuse to continue given this result? */
    blocksFlow: function (r) {
      if (!r) return false;
      var cfg = getConfig();
      if (r.status === 'unconfigured') return !!cfg.blockWhenUnconfigured;
      if (r.status === 'done') return false;
      return !!cfg.blockOnError;
    }
  };

  function isConfigured() {
    var cfg = getConfig();
    if (cfg.provider === 'cloudinary') {
      return !!(cfg.cloudName && cfg.uploadPreset);
    }
    if (cfg.provider === 'endpoint') {
      return !!cfg.endpoint;
    }
    return false;
  }

  if (!isConfigured()) {
    console.warn(
      '[rx-uploader] ' +
        VERSION +
        ' loaded without a storage provider. Prescription files will NOT be transmitted. ' +
        'Set window.ORVOIS_RX_UPLOAD = { provider, ... } before this asset loads.'
    );
  }
})();
